/**
 * CrazyGames SDK v3 — lifecycle hooks, game settings (e.g. muteAudio), and user account helpers.
 * Non-CrazyGames builds set VITE_WITH_CRAZYGAMES=false so this module never touches window.CrazyGames.
 */

import { devLog } from "./dev-log.js";

var initPromise = null;
var enabled = false;
var loadingNotified = false;
var muteSyncInstalled = false;
var gameplayActive = false;
/** @type {boolean|null} null until fetched; true when portal adblock detected */
var adblockPresent = null;

function isCrazyGamesBuild() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_CRAZYGAMES === "true";
}

function getSdk() {
  if (!isCrazyGamesBuild()) return null;
  return typeof window !== "undefined" && window.CrazyGames && window.CrazyGames.SDK
    ? window.CrazyGames.SDK
    : null;
}

function refreshEnabled() {
  var sdk = getSdk();
  if (!sdk) {
    enabled = false;
    return false;
  }
  var env = sdk.environment;
  enabled = env === "local" || env === "crazygames";
  return enabled;
}

function safeGameCall(methodName) {
  if (!enabled) return;
  var sdk = getSdk();
  if (!sdk || !sdk.game || typeof sdk.game[methodName] !== "function") return;
  try {
    devLog("[CrazyGamesBridge] CrazyGames.SDK.game." + methodName + "()");
    sdk.game[methodName]();
  } catch (e) {
    console.warn("[CrazyGamesBridge] " + methodName + " failed:", e);
  }
}

export const CrazyGamesBridge = {
  ensureInitialized: function () {
    if (initPromise) return initPromise;
    var sdk = getSdk();
    if (!sdk) {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    if (typeof sdk.init === "function") {
      devLog("[CrazyGamesBridge] CrazyGames.SDK.init()");
      initPromise = sdk.init().then(
        function () {
          refreshEnabled();
        },
        function (err) {
          enabled = false;
          console.warn("[CrazyGamesBridge] SDK init failed:", err);
        }
      );
    } else {
      initPromise = Promise.resolve().then(function () {
        refreshEnabled();
      });
    }
    return initPromise;
  },

  isEnabled: function () {
    return !!enabled;
  },

  /**
   * Fetches SDK.ad.hasAdblock() once after init. No-op when CrazyGames is disabled.
   * @returns {Promise<boolean>}
   */
  fetchAdblockStatus: function () {
    return CrazyGamesBridge.ensureInitialized().then(function () {
      if (!enabled) {
        adblockPresent = false;
        return false;
      }
      var sdk = getSdk();
      if (!sdk || !sdk.ad || typeof sdk.ad.hasAdblock !== "function") {
        adblockPresent = false;
        return false;
      }
      return sdk.ad.hasAdblock().then(
        function (result) {
          adblockPresent = !!result;
          return adblockPresent;
        },
        function (err) {
          console.warn("[CrazyGamesBridge] hasAdblock failed:", err);
          adblockPresent = false;
          return false;
        }
      );
    });
  },

  /** Sync read of last {@link fetchAdblockStatus} result; false when unknown or disabled. */
  hasAdblockPresent: function () {
    return !!adblockPresent;
  },

  /** True when rewarded ads can be requested on the CrazyGames portal (enabled, ad module, no adblock). */
  canOfferRewardedAds: function () {
    if (!enabled) return false;
    if (adblockPresent) return false;
    var sdk = getSdk();
    return !!(sdk && sdk.ad && typeof sdk.ad.requestAd === "function");
  },

  loadingStart: function () {
    if (!enabled) return;
    if (loadingNotified) return;
    loadingNotified = true;
    safeGameCall("loadingStart");
  },

  loadingStop: function () {
    if (!enabled) return;
    if (!loadingNotified) return;
    loadingNotified = false;
    safeGameCall("loadingStop");
  },

  gameplayStart: function () {
    if (!enabled || gameplayActive) return;
    safeGameCall("gameplayStart");
    gameplayActive = true;
  },

  gameplayStop: function () {
    if (!enabled || !gameplayActive) return;
    safeGameCall("gameplayStop");
    gameplayActive = false;
  },

  /**
   * Logged-in CrazyGames portal user, or null when unavailable / not signed in.
   * @returns {Promise<{ username: string, profilePictureUrl: string, __dangerousUserId: string } | null>}
   */
  getUser: function () {
    return CrazyGamesBridge.ensureInitialized().then(function () {
      if (!enabled) return null;
      var sdk = getSdk();
      if (!sdk || !sdk.user || !sdk.user.isUserAccountAvailable) return null;
      if (typeof sdk.user.getUser !== "function") return null;
      return sdk.user.getUser().then(
        function (user) {
          return user || null;
        },
        function (e) {
          console.warn("[CrazyGamesBridge] getUser failed:", e);
          return null;
        }
      );
    });
  },

  /**
   * CrazyGames account display name, or null when unavailable / not signed in.
   * @returns {Promise<string | null>}
   */
  getUsername: function () {
    return CrazyGamesBridge.getUser().then(function (user) {
      return user && typeof user.username === "string" ? user.username : null;
    });
  },

  /**
   * Subscribe to `SDK.game.settings.muteAudio` (HTML5 SDK). Matches portal mute toggle.
   * @param {{ setMuted?: (muted: boolean) => void }} soundManager
   */
  /**
   * Rewarded video ad. Resolves true when the player should receive the reward.
   * Non-CrazyGames / dev builds invoke callbacks immediately (grant reward).
   * `name` is accepted for call-site parity with Y8 placements and ignored by CrazyGames.
   * @param {{
   *   name?: string,
   *   onStarted?: () => void,
   *   onFinished?: () => void,
   *   onError?: (err: unknown) => void
   * }} [options]
   * @returns {Promise<boolean>}
   */
  requestRewardedAd: function (options) {
    var cbs = options || {};
    var started = typeof cbs.onStarted === "function" ? cbs.onStarted : null;
    var finished = typeof cbs.onFinished === "function" ? cbs.onFinished : null;
    var onError = typeof cbs.onError === "function" ? cbs.onError : null;

    function grant() {
      if (started) started();
      if (finished) finished();
      return true;
    }

    return CrazyGamesBridge.ensureInitialized().then(function () {
      if (!enabled) return grant();
      var sdk = getSdk();
      if (!sdk || !sdk.ad || typeof sdk.ad.requestAd !== "function") {
        if (onError) onError(new Error("rewarded ad unavailable"));
        return false;
      }
      return new Promise(function (resolve) {
        var settled = false;
        function settle(ok) {
          if (settled) return;
          settled = true;
          resolve(!!ok);
        }
        try {
          sdk.ad.requestAd("rewarded", {
            adStarted: function () {
              if (started) started();
            },
            adFinished: function () {
              if (finished) finished();
              settle(true);
            },
            adError: function (err) {
              console.warn("[CrazyGamesBridge] rewarded ad error:", err);
              if (onError) onError(err);
              settle(false);
            },
          });
        } catch (e) {
          console.warn("[CrazyGamesBridge] requestAd(rewarded) failed:", e);
          if (onError) onError(e);
          settle(false);
        }
      });
    });
  },

  installMuteSync: function (soundManager) {
    if (muteSyncInstalled) return;
    if (!soundManager || typeof soundManager.setMuted !== "function") return;

    CrazyGamesBridge.ensureInitialized().then(function () {
      if (muteSyncInstalled) return;
      if (!enabled) return;
      var sdk = getSdk();
      if (!sdk || !sdk.game) return;
      if (typeof sdk.game.addSettingsChangeListener !== "function") return;

      muteSyncInstalled = true;

      function applyMute(settings) {
        var m = settings && settings.muteAudio;
        soundManager.setMuted(m === true);
      }

      try {
        if (sdk.game.settings && typeof sdk.game.settings.muteAudio === "boolean") {
          applyMute(sdk.game.settings);
        } else {
          soundManager.setMuted(false);
        }
      } catch (e0) {
        console.warn("[CrazyGamesBridge] reading settings.muteAudio failed:", e0);
      }

      try {
        sdk.game.addSettingsChangeListener(function (newSettings) {
          applyMute(newSettings);
        });
      } catch (e) {
        muteSyncInstalled = false;
        console.warn("[CrazyGamesBridge] addSettingsChangeListener failed:", e);
      }
    });
  },
};